import {Box, Typography} from '@mui/material';
import {useEffect, useState, useCallback, useMemo} from 'react';
import {CONNECTION_STATE_CHANGE, PubSub} from '@aws-amplify/pubsub';
import {useJsApiLoader, GoogleMap, Marker, Polyline} from '@react-google-maps/api';
import {useSearchParams} from 'react-router-dom';
import {Hub} from 'aws-amplify/utils';
import {IoTDataPlaneClient, GetRetainedMessageCommand} from '@aws-sdk/client-iot-data-plane';
import {fetchAuthSession} from 'aws-amplify/auth';
import {ArrowOutward} from '@mui/icons-material';

const {VITE_USER_POOL_REGION, VITE_GOOGLE_MAPS_API_KEY, VITE_IOT_ENDPOINT} =
    import.meta.env;

const containerStyle = {
    width: '100vw',
    height: '100dvh', // Use dynamic viewport height for mobile
    borderRadius: '0px',
    overflow: 'hidden',
};

const defaultCenter = {
    lat: 17.4065,
    lng: 78.4772,
};

type Coordinates = {
    latitude: number;
    longitude: number;
    date: string;
    id: string;
};

const pubsub = new PubSub({
    region: VITE_USER_POOL_REGION,
    endpoint: `wss://${VITE_IOT_ENDPOINT}/mqtt`,
});

interface DriverParams {
    id: string;
    name: string;
}

const decodeDriverParams = (encodedString: string | null): DriverParams | null => {
    if (!encodedString) return null;

    try {
        const decodedJson = atob(decodeURIComponent(encodedString));
        const parsedJson = JSON.parse(decodedJson);
        if (
            parsedJson &&
            typeof parsedJson === 'object' &&
            typeof parsedJson.id === 'string' &&
            typeof parsedJson.name === 'string'
        ) {
            return parsedJson as DriverParams;
        }

        console.error("Invalid structure in decoded driver params");
        return null;
    } catch (error) {
        console.error("Error decoding driver params:", error);
        return null;
    }
};

const formatRelativeDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Reset time to compare dates only
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

    const time = date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
    });

    if (dateOnly.getTime() === todayOnly.getTime()) {
        return `Today at ${time}`;
    } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
        return `Yesterday at ${time}`;
    } else {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year} at ${time}`;
    }
};

// Simple Google Maps directions URL - works on both desktop and mobile
const getDirectionsUrl = (driverLat: number, driverLng: number): string => {
    return `https://www.google.com/maps/dir/?api=1&destination=${driverLat},${driverLng}&travelmode=driving`;
};

// Location caching utilities for polyline tracking
interface CachedLocation {
    latitude: number;
    longitude: number;
    timestamp: number;
    id: string;
}

// Get cache key for driver
const getCacheKey = (driverId: string): string => `driver_locations_${driverId}`;

// Get locations from cache within 2-hour window
const getCachedLocations = (driverId: string): CachedLocation[] => {
    try {
        const cacheKey = getCacheKey(driverId);
        const cached = localStorage.getItem(cacheKey);
        if (!cached) return [];

        const locations: CachedLocation[] = JSON.parse(cached);
        const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000); // 2 hours in milliseconds
        
        // Filter out locations older than 2 hours
        const recentLocations = locations.filter(loc => loc.timestamp > twoHoursAgo);
        
        // If we filtered out some locations, update the cache
        if (recentLocations.length !== locations.length) {
            localStorage.setItem(cacheKey, JSON.stringify(recentLocations));
        }
        
        return recentLocations.sort((a, b) => a.timestamp - b.timestamp); // Sort by timestamp
    } catch (error) {
        console.error('Error getting cached locations:', error);
        return [];
    }
};

// Add new location to cache
const addLocationToCache = (driverId: string, location: Coordinates): void => {
    try {
        const cacheKey = getCacheKey(driverId);
        const existingLocations = getCachedLocations(driverId);
        
        const newLocation: CachedLocation = {
            latitude: location.latitude,
            longitude: location.longitude,
            timestamp: Number(location.date),
            id: location.id
        };
        
        // Check if this location already exists (avoid duplicates)
        const existsIndex = existingLocations.findIndex(loc => 
            Math.abs(loc.latitude - newLocation.latitude) < 0.00001 && 
            Math.abs(loc.longitude - newLocation.longitude) < 0.00001 &&
            Math.abs(loc.timestamp - newLocation.timestamp) < 30000 // Within 30 seconds
        );
        
        if (existsIndex === -1) {
            // Add new location
            existingLocations.push(newLocation);
            
            // Keep only recent locations (2 hours) and limit to 500 points for performance
            const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);
            const recentLocations = existingLocations
                .filter(loc => loc.timestamp > twoHoursAgo)
                .sort((a, b) => a.timestamp - b.timestamp)
                .slice(-500); // Keep last 500 points max
            
            localStorage.setItem(cacheKey, JSON.stringify(recentLocations));
        }
    } catch (error) {
        console.error('Error adding location to cache:', error);
        // If localStorage is full, clear old data and try again
        if (error instanceof Error && error.name === 'QuotaExceededError') {
            try {
                const cacheKey = getCacheKey(driverId);
                const locations = getCachedLocations(driverId).slice(-100); // Keep only last 100 points
                localStorage.setItem(cacheKey, JSON.stringify(locations));
            } catch (retryError) {
                console.error('Failed to clear cache:', retryError);
            }
        }
    }
};

const refreshRetainedLocation = async (
    topicName: string, 
    currentPosition: Coordinates | null, 
    retryCount: number = 0
): Promise<Coordinates | null> => {
    const maxRetries = 3;
    const retryDelays = [1000, 2000, 5000]; // Progressive delay: 1s, 2s, 5s
    
    try {
        const retainedData = await getRetainedMessage(topicName);
        
        if (retainedData) {
            // Only update if retained location is different from current position
            const isDifferentLocation = !currentPosition || 
                currentPosition.latitude !== retainedData.latitude || 
                currentPosition.longitude !== retainedData.longitude;
                
            if (isDifferentLocation) {
                return retainedData;
            }
        }
        return currentPosition;
    } catch (error) {
        console.error(`Error refreshing retained location (attempt ${retryCount + 1}):`, error);
        
        // Retry with progressive backoff if not exceeded max retries
        if (retryCount < maxRetries) {
            const delay = retryDelays[retryCount];
            
            await new Promise(resolve => setTimeout(resolve, delay));
            return refreshRetainedLocation(topicName, currentPosition, retryCount + 1);
        }
        
        // Return current position if all retries failed
        console.error('All retry attempts failed for retained location refresh');
        return currentPosition;
    }
};

const getRetainedMessage = async (topicName: string): Promise<Coordinates | null> => {
    try {
        // Authenticate and get credentials
        const session = await fetchAuthSession();
        const credentials = session.credentials;
        
        if (!credentials) {
            throw new Error('No credentials available');
        }

        // Create IoT Data Plane client
        const iotDataPlaneClient = new IoTDataPlaneClient({
            region: VITE_USER_POOL_REGION,
            credentials: {
                accessKeyId: credentials.accessKeyId,
                secretAccessKey: credentials.secretAccessKey,
                sessionToken: credentials.sessionToken,
            },
            endpoint: `https://${VITE_IOT_ENDPOINT}`,
        });

        // Get retained message
        const command = new GetRetainedMessageCommand({
            topic: topicName,
        });

        const response = await iotDataPlaneClient.send(command);
        
        if (response.payload && response.payload.length > 0) {
            const decoder = new TextDecoder();
            const messageStr = decoder.decode(response.payload);
            const messageData = JSON.parse(messageStr);
            
            const latitude = Number(messageData.latitude);
            const longitude = Number(messageData.longitude);
            const id = messageData.id;
            const timestamp = Number(messageData.timestamp);
            
            if (Number.isFinite(latitude) && Number.isFinite(longitude) && id && timestamp) {
                // Check if location is older than 12 hours
                const now = Date.now();
                const locationAge = now - timestamp;
                const twelveHoursInMs = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
                
                if (locationAge > twelveHoursInMs) {
                    return null;
                }
                
                return {
                    latitude,
                    longitude,
                    id: String(id),
                    date: String(timestamp),
                };
            }
        }
        
        return null;
    } catch (error) {
        console.error('Error fetching retained message:', error);
        return null;
    }
};


const DriverLocationPage = () => {
    const [searchParams] = useSearchParams();
    const driverParams = decodeDriverParams(searchParams.get('zid'))
    const driverID = driverParams?.id || null
    const topicName = driverID ? `location/drivers/${driverID}` : null
    const name = driverParams?.name || null

    useEffect(() => {
        document.title = 'Location';
    }, []);

    // Load cached locations for polyline on component mount and setup periodic cleanup
    useEffect(() => {
        if (!driverID) return;
        
        const loadCachedLocations = () => {
            const cachedLocations = getCachedLocations(driverID);
            if (cachedLocations.length > 0) {
                const pathCoordinates = cachedLocations.map(loc => ({
                    lat: loc.latitude,
                    lng: loc.longitude
                }));
                setPolylinePath(pathCoordinates);
            }
        };


        // Load initially
        loadCachedLocations();

        // Set up periodic cleanup every 10 minutes to remove old data
        const cleanupInterval = setInterval(() => {
            loadCachedLocations(); // This will trigger cleanup in getCachedLocations
        }, 10 * 60 * 1000); // 10 minutes

        return () => {
            clearInterval(cleanupInterval);
        };
    }, [driverID]);

    const {isLoaded} = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: VITE_GOOGLE_MAPS_API_KEY,
    });

    const [markerPosition, setMarkerPosition] = useState<Coordinates | null>(
        null
    );
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [retainedMessageFetched, setRetainedMessageFetched] = useState<boolean>(false);
    const [locationStatus, setLocationStatus] = useState<'fetching' | 'waiting' | 'offline' | 'online' | 'retained'>('fetching');
    const [isLiveLocation, setIsLiveLocation] = useState<boolean>(false);
    const [lastMessageTime, setLastMessageTime] = useState<number | null>(null);
    const [offlineCheckInterval, setOfflineCheckInterval] = useState<number | null>(null);
    const [polylinePath, setPolylinePath] = useState<{lat: number, lng: number}[]>([]);

    // Create time-based color gradient segments (green to yellow)
    const coloredSegments = useMemo(() => {
        if (polylinePath.length < 2 || !driverID) return [];
        
        // Get cached locations with timestamps
        const cachedLocations = getCachedLocations(driverID);
        if (cachedLocations.length < 2) return [];
        
        const now = Date.now();
        const twoMinutesAgo = now - (2 * 60 * 1000); // 2 minutes in milliseconds - keep green
        const tenMinutesAgo = now - (10 * 60 * 1000); // 10 minutes - fade to yellow
        const twoHoursAgo = now - (2 * 60 * 60 * 1000); // 2 hours in milliseconds
        
        const segments = [];
        const segmentSize = Math.max(8, Math.floor(cachedLocations.length / 8)); // Up to 8 segments
        
        for (let i = 0; i < cachedLocations.length - 1; i += segmentSize) {
            const endIndex = Math.min(i + segmentSize + 1, cachedLocations.length);
            const segmentLocations = cachedLocations.slice(i, endIndex);
            
            if (segmentLocations.length < 2) continue;
            
            // Use the newest timestamp in this segment for color calculation
            const segmentTimestamp = segmentLocations[segmentLocations.length - 1].timestamp;
            const segmentPath = segmentLocations.map(loc => ({ lat: loc.latitude, lng: loc.longitude }));
            
            let color, opacity, weight;
            
            if (segmentTimestamp >= twoMinutesAgo) {
                // Last 2 minutes - vibrant green
                color = '#00E676';
                opacity = 0.9;
                weight = 4;
            } else if (segmentTimestamp >= tenMinutesAgo) {
                // 2-10 minutes - fade from green to yellow
                const ageRatio = (twoMinutesAgo - segmentTimestamp) / (twoMinutesAgo - tenMinutesAgo);
                
                // Interpolate from green (#00E676) to yellow (#FFD54F)
                const greenR = 0x00, greenG = 0xE6, greenB = 0x76;
                const yellowR = 0xFF, yellowG = 0xD5, yellowB = 0x4F;
                
                const r = Math.round(greenR + (yellowR - greenR) * ageRatio);
                const g = Math.round(greenG + (yellowG - greenG) * ageRatio);
                const b = Math.round(greenB + (yellowB - greenB) * ageRatio);
                
                color = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                opacity = Math.max(0.5, 0.9 - ageRatio * 0.3);
                weight = Math.max(3, 4 - ageRatio * 1);
            } else {
                // Older than 10 minutes - yellow and fading
                const ageRatio = Math.min(1, (tenMinutesAgo - segmentTimestamp) / (tenMinutesAgo - twoHoursAgo));
                color = '#FFD54F';
                opacity = Math.max(0.3, 0.6 - ageRatio * 0.3);
                weight = Math.max(2, 3 - ageRatio);
            }
            
            segments.push({
                path: segmentPath,
                color,
                opacity,
                weight,
                isRecent: segmentTimestamp >= twoMinutesAgo, // Only 2-minute threshold for animations
            });
        }
        
        return segments;
    }, [polylinePath, driverID]);

    // Memoized custom icon to prevent unnecessary re-renders
    const customIcon = useMemo(() => {
        if (!isLoaded) return undefined;
        
        return {
            path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
            fillColor: isLiveLocation ? '#4CAF50' : '#FF9500', // Green for live, orange for retained
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
            strokeOpacity: 1,
            scale: 1.8,
            anchor: new window.google.maps.Point(12, 22), // Pin point at bottom
        };
    }, [isLoaded, isLiveLocation]);
    
    // Memoized pulse ring for live locations to prevent unnecessary re-renders
    const pulseRing = useMemo(() => {
        if (!isLoaded || !isLiveLocation) return undefined;
        
        return {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: 'transparent',
            fillOpacity: 0,
            strokeColor: '#4CAF50',
            strokeWeight: 2,
            strokeOpacity: 0.4,
            scale: 20,
        };
    }, [isLoaded, isLiveLocation]);

    // Authentication and retained message fetching effect
    useEffect(() => {
        const authenticateAndFetchRetained = async () => {
            if (!topicName) return;

            try {
                // Authenticate first
                const session = await fetchAuthSession();
                if (session.credentials) {
                    setIsAuthenticated(true);

                    // Fetch retained message
                    const retainedData = await getRetainedMessage(topicName);
                    if (retainedData) {
                        setMarkerPosition(retainedData);
                        setLocationStatus('retained');
                        setIsLiveLocation(false);
                        
                        // Set initial lastMessageTime based on retained message timestamp
                        const retainedTimestamp = Number(retainedData.date);
                        setLastMessageTime(retainedTimestamp);
                        
                        // Center map on retained location if map is ready
                        if (map) {
                            map.panTo({lat: retainedData.latitude, lng: retainedData.longitude});
                        }
                    } else {
                        setLocationStatus('waiting');
                        
                        // Set timeout to mark as offline if no live updates in 30 seconds
                        setTimeout(() => {
                            setLocationStatus(prev => prev === 'waiting' ? 'offline' : prev);
                        }, 30000);
                    }
                    
                    setRetainedMessageFetched(true);
                }
            } catch (error) {
                console.error('Authentication or retained message fetch failed:', error);
                setLocationStatus('waiting');
                setRetainedMessageFetched(true); // Continue with live updates even if retained fetch fails
                
                // Set timeout to mark as offline if no live updates in 30 seconds
                setTimeout(() => {
                    setLocationStatus(prev => prev === 'waiting' ? 'offline' : prev);
                }, 30000);
            }
        };

        if (!isAuthenticated && !retainedMessageFetched) {
            authenticateAndFetchRetained();
        }
    }, [topicName, map, isAuthenticated, retainedMessageFetched]);

    // Heartbeat monitoring effect - checks for offline status every 30 seconds
    useEffect(() => {
        if (!isAuthenticated || !retainedMessageFetched || !topicName) return;

        const checkOfflineStatus = async () => {
            if (!lastMessageTime) return;

            const now = Date.now();
            const timeSinceLastMessage = now - lastMessageTime;
            const thirtySecondsInMs = 30 * 1000;

            // If driver was online but no message received in 30 seconds
            if (locationStatus === 'online' && timeSinceLastMessage > thirtySecondsInMs) {
                // Refresh retained location and switch to offline/retained status
                const updatedRetainedLocation = await refreshRetainedLocation(topicName, markerPosition);
                if (updatedRetainedLocation) {
                    setMarkerPosition(updatedRetainedLocation);
                    // Smoothly update map center without jarring movement
                    if (map) {
                        map.panTo({lat: updatedRetainedLocation.latitude, lng: updatedRetainedLocation.longitude});
                    }
                }
                
                setLocationStatus('retained');
                setIsLiveLocation(false);
            }
        };

        // Set up interval for continuous monitoring
        const interval = setInterval(checkOfflineStatus, 5000); // Check every 5 seconds for responsiveness
        setOfflineCheckInterval(interval as unknown as number);

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [topicName, lastMessageTime, locationStatus, markerPosition, map, isAuthenticated, retainedMessageFetched]);

    // Cleanup effect for intervals
    useEffect(() => {
        return () => {
            if (offlineCheckInterval) {
                clearInterval(offlineCheckInterval);
            }
        };
    }, [offlineCheckInterval]);

    // PubSub subscription effect - only start after retained message is fetched
    useEffect(() => {
        if (!isAuthenticated || !retainedMessageFetched || !topicName) return;

        const subscription = pubsub
            .subscribe({topics: [String(topicName)]})
            .subscribe({
                next: (data) => {
                    try {
                        if (!data || typeof data !== 'object') {
                            throw new Error('Invalid data format');
                        }
                        const latitude = Number(data.latitude);
                        const longitude = Number(data.longitude);
                        const id = data.id;

                        if (
                            !Number.isFinite(latitude) ||
                            !Number.isFinite(longitude) ||
                            !id
                        ) {
                            throw new Error('Invalid coordinates or ID');
                        }

                        const newPosition = {
                            latitude,
                            longitude,
                            id: String(data.id),
                            date: String(data.timestamp),
                        };
                        
                        // Check if the timestamp is recent (within last 10 minutes) to determine if it's truly live
                        // Also check if this is a newer message than what we currently have
                        const now = Date.now();
                        const messageTimestamp = Number(data.timestamp);
                        const tenMinutesInMs = 10 * 60 * 1000; // 10 minutes in milliseconds
                        const isRecentUpdate = (now - messageTimestamp) <= tenMinutesInMs;
                        
                        // Check if this is a newer message than our current position
                        const currentTimestamp = markerPosition ? Number(markerPosition.date) : 0;
                        const isNewerMessage = messageTimestamp > currentTimestamp;
                        
                        // Only update if this is a newer message
                        if (isNewerMessage) {
                            setMarkerPosition(newPosition);
                            
                            // Update lastMessageTime for heartbeat monitoring
                            setLastMessageTime(now);
                            
                            // Cache the new location for polyline
                            if (driverID) {
                                addLocationToCache(driverID, newPosition);
                            }
                            
                            // Update polyline path
                            setPolylinePath(prevPath => {
                                const newPoint = {lat: latitude, lng: longitude};
                                
                                // Avoid adding duplicate points (within 5 meters)
                                if (prevPath.length > 0) {
                                    const lastPoint = prevPath[prevPath.length - 1];
                                    const distance = Math.abs(lastPoint.lat - newPoint.lat) + Math.abs(lastPoint.lng - newPoint.lng);
                                    if (distance < 0.00005) { // ~5 meters
                                        return prevPath;
                                    }
                                }
                                
                                // Keep only recent points for performance (last 500 points)
                                const updatedPath = [...prevPath, newPoint].slice(-500);
                                return updatedPath;
                            });
                            
                            if (isRecentUpdate) {
                                // This is a recent and newer message - driver is live
                                setLocationStatus('online');
                                setIsLiveLocation(true);
                            } else {
                                // This is newer but not recent enough - still update position but keep as retained
                                setLocationStatus('retained');
                                setIsLiveLocation(false);
                            }

                            // Smooth map update - use panTo instead of setCenter for better UX
                            if (map) {
                                const currentCenter = map.getCenter();
                                const newLatLng = {lat: latitude, lng: longitude};
                                
                                // Only pan if the location has changed significantly (> 10 meters approximately)
                                if (!currentCenter || 
                                    Math.abs(currentCenter.lat() - latitude) > 0.0001 || 
                                    Math.abs(currentCenter.lng() - longitude) > 0.0001) {
                                    map.panTo(newLatLng);
                                }
                            }
                        }
                    } catch (error) {
                        console.error('Error parsing MQTT message:', error, 'Raw data:', data);
                        
                        // Don't update anything if message parsing fails
                        // This prevents corrupted data from affecting the UI
                    }
                },
                error: (error) => {
                    console.error('MQTT Subscription error:', error);
                    
                    // Set location status to indicate connection issues
                    setLocationStatus(prevStatus => {
                        if (prevStatus === 'online') {
                            return 'retained'; // Gracefully degrade to retained location
                        }
                        return prevStatus;
                    });
                    
                    // Attempt to refresh retained location if we lose connection
                    if (markerPosition) {
                        refreshRetainedLocation(topicName, markerPosition)
                            .then(updatedLocation => {
                                if (updatedLocation && updatedLocation !== markerPosition) {
                                    setMarkerPosition(updatedLocation);
                                    if (map) {
                                        map.panTo({
                                            lat: updatedLocation.latitude, 
                                            lng: updatedLocation.longitude
                                        });
                                    }
                                }
                            })
                            .catch(err => console.error('Failed to refresh retained location after connection error:', err));
                    }
                },
            });

        return () => {
            subscription.unsubscribe();
        };
    }, [topicName, driverID, map, isAuthenticated, retainedMessageFetched]);

    useEffect(() => {
        const hubListener = Hub.listen('pubsub', ({payload}) => {
            if (payload.event === CONNECTION_STATE_CHANGE) {
                // Connection state change handled if needed in the future
            }
        });

        return () => hubListener();
    }, []);

    const onLoad = useCallback((mapInstance: google.maps.Map) => {
        setMap(mapInstance);
    }, []);

    const onUnmount = useCallback(() => setMap(null), []);

    // Handle directions button click - opens Google Maps directly
    const handleGetDirections = useCallback(() => {
        if (!markerPosition) return;
        
        const directionsUrl = getDirectionsUrl(
            markerPosition.latitude,
            markerPosition.longitude
        );
        
        // Opens Google Maps app on mobile, new tab on desktop
        window.open(directionsUrl, '_blank');
    }, [markerPosition]);


    return (
        <Box
            className="dvh-fallback"
            sx={{
                width: '100vw',
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 1000,
                backgroundColor: '#FFF8E1',
                // Remove overflow: hidden to preserve pull-to-refresh
            }}
        >
            {/* Brand Logo - Top Left */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    zIndex: 1002,
                }}
            >
                <img
                    src='./zero-logo.png'
                    alt='Zero Logo'
                    style={{
                        width: '48px',
                        height: 'auto',
                        filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))',
                    }}
                />
            </Box>

            {/* Driver Information Header - Overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1001,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    padding: 2,
                    paddingTop: 3,
                    borderBottom: '1px solid rgba(255, 212, 57, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                }}
            >
                {/* Driver Information */}
                <Box sx={{ textAlign: 'center' }}>
                    <Typography
                        variant='subtitle1'
                        sx={{
                            fontWeight: 'bold',
                            color: '#2C3E50',
                        }}
                    >
                        {name}
                    </Typography>
                    
                    {/* Location Status and Time */}
                    {markerPosition && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <Typography
                                variant='body2'
                                sx={{
                                    color: isLiveLocation ? '#4CAF50' : '#FF9800',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                {isLiveLocation ? 'Live' : 'Last Known'}
                            </Typography>
                            <Typography
                                variant='body2'
                                sx={{
                                    color: '#999',
                                    fontSize: '0.75rem',
                                }}
                            >
                                • {formatRelativeDate(parseInt(markerPosition.date))}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Loading/Error States - Centered Overlay */}
            {(locationStatus === 'fetching' || locationStatus === 'waiting' || locationStatus === 'offline') && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1002,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        padding: 4,
                        borderRadius: 3,
                        textAlign: 'center',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        border: '1px solid rgba(255, 212, 57, 0.2)',
                        minWidth: 280,
                    }}
                >
                    {locationStatus === 'fetching' && (
                        <>
                            <Box sx={{ fontSize: '3rem', mb: 2 }}>🔍</Box>
                            <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 600, mb: 1 }}>
                                Locating Driver
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Connecting to GPS tracking system...
                            </Typography>
                        </>
                    )}
                    
                    {locationStatus === 'waiting' && (
                        <>
                            <Box sx={{ fontSize: '3rem', mb: 2 }}>⏱️</Box>
                            <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 600, mb: 1 }}>
                                Waiting for Signal
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Listening for driver's location updates...
                            </Typography>
                        </>
                    )}
                    
                    {locationStatus === 'offline' && (
                        <>
                            <Box sx={{ fontSize: '3rem', mb: 2 }}>📵</Box>
                            <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 600, mb: 1 }}>
                                Driver Offline
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Unable to connect to driver's tracking device
                            </Typography>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    padding: '8px 16px',
                                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                    borderRadius: 2,
                                    border: '1px solid rgba(211, 47, 47, 0.2)',
                                }}
                            >
                                <Typography variant="body2" sx={{ color: '#D32F2F', fontWeight: 500 }}>
                                    Location Unavailable
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            )}

            {/* Full Screen Map */}
            {isLoaded && (locationStatus === 'online' || locationStatus === 'retained') && (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                    }}
                >
                        {/* Location Status Overlay */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 12,
                                left: 12,
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                backgroundColor: isLiveLocation 
                                    ? 'rgba(76, 175, 80, 0.95)' 
                                    : 'rgba(255, 152, 0, 0.95)',
                                backdropFilter: 'blur(4px)',
                                padding: '6px 10px',
                                borderRadius: 20,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                border: '1px solid rgba(255,255,255,0.2)',
                            }}
                        >
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    animation: isLiveLocation ? 'pulse 2s infinite' : 'none',
                                }}
                            />
                            <Typography
                                variant='body2'
                                sx={{
                                    color: '#fff',
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                {isLiveLocation ? 'LIVE' : 'LAST KNOWN'}
                            </Typography>
                        </Box>

                        {/* Check Arrival Time Button - Bottom Right */}
                        <Box
                            onClick={handleGetDirections}
                            className="mobile-fixed-button"
                            sx={{
                                position: 'fixed',
                                // Simplified mobile positioning that works on real devices
                                bottom: '20px',
                                right: '16px',
                                zIndex: 9999,
                                backgroundColor: 'rgba(255, 212, 57, 0.95)',
                                backdropFilter: 'blur(8px)',
                                padding: '14px 18px',
                                borderRadius: '28px',
                                boxShadow: '0 6px 20px rgba(255, 212, 57, 0.5)',
                                border: '2px solid rgba(255,255,255,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                minHeight: '48px',
                                minWidth: '48px',
                                // Touch device optimizations
                                WebkitTapHighlightColor: 'transparent',
                                touchAction: 'manipulation',
                                userSelect: 'none',
                                // Responsive adjustments
                                '@media (max-width: 480px)': {
                                    bottom: '16px',
                                    right: '12px',
                                    padding: '12px 16px',
                                },
                                '@media (max-height: 600px)': {
                                    bottom: '12px',
                                    right: '12px',
                                    padding: '10px 14px',
                                },
                                // Desktop view in mobile browser - ensure button stays visible
                                '@media (min-width: 768px)': {
                                    bottom: '24px',
                                    right: '24px',
                                },
                                // For very wide mobile screens in landscape
                                '@media (min-width: 768px) and (max-height: 500px)': {
                                    bottom: '16px',
                                    right: '16px',
                                    padding: '10px 14px',
                                },
                                // iPhone SE and similar small screens
                                '@media (max-width: 375px) and (max-height: 667px)': {
                                    bottom: '14px',
                                    right: '10px',
                                    padding: '10px 12px',
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 193, 7, 0.98)',
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 6px 20px rgba(255, 212, 57, 0.6)',
                                },
                                '&:active': {
                                    transform: 'scale(0.98)',
                                    backgroundColor: 'rgba(255, 193, 7, 1)',
                                },
                                transition: 'all 0.3s ease-in-out',
                            }}
                        >
                            <ArrowOutward sx={{ 
                                color: '#2C3E50', 
                                fontSize: '1.1rem',
                                fontWeight: 'bold'
                            }} />
                            <Typography
                                variant="button"
                                sx={{
                                    color: '#2C3E50',
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    // Hide text on very small screens to save space
                                    '@media (max-width: 400px)': {
                                        display: 'none'
                                    }
                                }}
                            >
                                Check Arrival Time
                            </Typography>
                        </Box>
                        
                        <GoogleMap
                            mapContainerStyle={containerStyle}
                            center={
                                markerPosition
                                    ? {
                                        lat: markerPosition.latitude,
                                        lng: markerPosition.longitude,
                                    }
                                    : defaultCenter
                            }
                            zoom={18}
                            onLoad={onLoad}
                            onUnmount={onUnmount}
                            options={{
                                // Clean interface - disable all map controls since we're already full-screen
                                disableDefaultUI: true,
                                fullscreenControl: false,
                                zoomControl: false,
                                streetViewControl: false,
                                mapTypeControl: false,
                                rotateControl: false,
                                scaleControl: false,
                                panControl: false,
                                keyboardShortcuts: true,
                                // Clean map styling
                                styles: [
                                    {
                                        featureType: "poi",
                                        elementType: "labels",
                                        stylers: [{ visibility: "off" }]
                                    }
                                ]
                            }}
                        >
                            {/* Elegant Time-Based Gradient Trail (Green to Yellow) */}
                            {coloredSegments.length > 0 && (
                                <>
                                    {coloredSegments.map((segment, index) => (
                                        <div key={`gradient-segment-${index}`}>
                                            {/* Subtle shadow for depth */}
                                            <Polyline
                                                path={segment.path}
                                                options={{
                                                    strokeColor: '#000000',
                                                    strokeOpacity: segment.opacity * 0.12,
                                                    strokeWeight: segment.weight + 1.5,
                                                    geodesic: true,
                                                    zIndex: 1,
                                                }}
                                            />
                                            
                                            {/* Main colored trail segment */}
                                            <Polyline
                                                path={segment.path}
                                                options={{
                                                    strokeColor: segment.color,
                                                    strokeOpacity: segment.opacity,
                                                    strokeWeight: segment.weight,
                                                    geodesic: true,
                                                    zIndex: 2,
                                                    icons: segment.isRecent ? [ // Only recent segments get animations
                                                        // Subtle flowing dots
                                                        {
                                                            icon: {
                                                                path: window.google.maps.SymbolPath.CIRCLE,
                                                                scale: 2,
                                                                strokeColor: '#FFFFFF',
                                                                strokeOpacity: 0.8,
                                                                strokeWeight: 1,
                                                                fillColor: segment.color,
                                                                fillOpacity: 0.9,
                                                            },
                                                            offset: '0%',
                                                            repeat: '60px',
                                                        },
                                                        // Minimal direction arrows
                                                        {
                                                            icon: {
                                                                path: 'M -1,0 1,0',
                                                                strokeColor: '#FFFFFF',
                                                                strokeOpacity: 0.6,
                                                                strokeWeight: 2,
                                                                scale: 1.5,
                                                            },
                                                            offset: '30px',
                                                            repeat: '120px',
                                                        },
                                                    ] : [], // Older segments are clean without animations
                                                }}
                                            />
                                        </div>
                                    ))}
                                    
                                    {/* Subtle highlight for the very latest segment (if live) */}
                                    {isLiveLocation && coloredSegments.length > 0 && (
                                        <Polyline
                                            path={coloredSegments[coloredSegments.length - 1].path.slice(-5)} // Last 5 points
                                            options={{
                                                strokeColor: '#00E676',
                                                strokeOpacity: 0.4,
                                                strokeWeight: 6,
                                                geodesic: true,
                                                zIndex: 1,
                                            }}
                                        />
                                    )}
                                </>
                            )}

                            {markerPosition && (
                                <>
                                    {/* Subtle pulse ring for live locations */}
                                    {pulseRing && (
                                        <Marker
                                            position={{
                                                lat: markerPosition?.latitude,
                                                lng: markerPosition?.longitude,
                                            }}
                                            icon={pulseRing}
                                            zIndex={1}
                                        />
                                    )}
                                    {/* Main location pin marker */}
                                    <Marker
                                        position={{
                                            lat: markerPosition?.latitude,
                                            lng: markerPosition?.longitude,
                                        }}
                                        icon={customIcon}
                                        title={isLiveLocation ? `${name} - Live Location` : `${name} - Last Known Location`}
                                        zIndex={2}
                                    />
                                </>
                            )}
                        </GoogleMap>
                    </Box>
                )}
        </Box>
    );
};

export default DriverLocationPage;